import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface FinalCTASectionProps {
  title?: string;
  subtitle?: string;
  ctaCity?: string;
  showForm?: boolean;
}

interface FormState {
  status: 'idle' | 'loading' | 'success' | 'error';
}

export default function FinalCTASection({
  title = 'Get a Cleaner, Healthier Facility Today',
  subtitle = 'Request your free assessment and custom cleaning plan. No obligation — fast response guaranteed.',
  ctaCity,
  showForm = true,
}: FinalCTASectionProps) {
  const { ref, isVisible } = useScrollAnimation();
  const [formState, setFormState] = useState<FormState>({ status: 'idle' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState({ status: 'loading' });
    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value.toString()));
    try {
      const res = await fetch('https://readdy.ai/api/form/d6uqtim7ipaugi04ldhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setFormState({ status: 'success' });
        form.reset();
      } else {
        setFormState({ status: 'error' });
      }
    } catch {
      setFormState({ status: 'error' });
    }
  };

  const quoteLabel = ctaCity ? `Get a Free Quote in ${ctaCity}` : 'Get a Free Quote Today';

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/c110432d1f744714f0331f2d9a013363.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#03143A]/93" />
      </div>

      {/* Decorative accent */}
      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-transparent via-[#C70532] to-transparent" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`grid lg:grid-cols-2 gap-14 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Left side */}
          <div className="pt-4">
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-[#C70532]/20 border border-[#C70532]/30 rounded-full mb-7">
              <div className="w-2 h-2 rounded-full bg-[#C70532] animate-pulse" />
              <span className="text-[#C70532] text-xs font-bold uppercase tracking-widest">Take Action Today — Limited Slots Available</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">{title}</h2>
            <p className="text-white/65 text-lg mb-10 leading-relaxed">{subtitle}</p>

            <div className="flex flex-wrap gap-4 mb-12">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer group"
              >
                {quoteLabel}
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              <a
                href="tel:+17133382553"
                className="inline-flex items-center gap-2.5 px-8 py-4 border-2 border-white/25 hover:border-white text-white font-bold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-[#C70532]" />
                Call (713) 338-2553
              </a>
            </div>

            {/* Trust checklist */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: 'ri-check-double-line', label: 'No Obligation Assessment' },
                { icon: 'ri-time-line', label: '24-Hour Response Time' },
                { icon: 'ri-shield-check-line', label: 'Fully Licensed & Insured' },
                { icon: 'ri-calendar-line', label: 'Flexible Start Date' },
                { icon: 'ri-hand-coin-line', label: 'Customized Pricing' },
                { icon: 'ri-customer-service-2-line', label: '24/7 Emergency Services' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5 text-white/65 text-sm">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#C70532]`} />
                  </div>
                  {item.label}
                </div>
              ))}
            </div>
          </div>

          {/* Right side — Lead form */}
          {showForm && (
            <div className={`bg-white rounded-3xl p-9 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '200ms' }}>
              <h3 className="text-2xl font-black text-[#03143A] mb-1.5">Request Service Online</h3>
              <p className="text-gray-500 text-sm mb-7">Fill out the form — we&apos;ll respond within 24 hours with a custom proposal.</p>

              {formState.status === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-green-600 text-2xl" />
                  </div>
                  <h4 className="font-bold text-[#03143A] text-xl mb-2">Request Submitted!</h4>
                  <p className="text-gray-500 text-sm">We&apos;ll contact you within 24 hours to discuss your facility needs and provide a free quote.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-[#03143A] mb-1.5 uppercase tracking-wide">Full Name *</label>
                      <input type="text" name="name" required placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors bg-[#FAF8F5]" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#03143A] mb-1.5 uppercase tracking-wide">Phone *</label>
                      <input type="tel" name="phone" required placeholder="(713) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors bg-[#FAF8F5]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#03143A] mb-1.5 uppercase tracking-wide">Email *</label>
                    <input type="email" name="email" required placeholder="you@company.com" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors bg-[#FAF8F5]" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#03143A] mb-1.5 uppercase tracking-wide">Company Name</label>
                    <input type="text" name="company" placeholder="Your Company Inc." className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors bg-[#FAF8F5]" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#03143A] mb-1.5 uppercase tracking-wide">Service Needed</label>
                    <select name="service" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors bg-[#FAF8F5] cursor-pointer">
                      <option value="">Select a service...</option>
                      <option value="Commercial Cleaning">Commercial Cleaning</option>
                      <option value="Janitorial Services">Janitorial Services</option>
                      <option value="Office Cleaning">Office Cleaning</option>
                      <option value="Floor Care">Floor Care &amp; Maintenance</option>
                      <option value="Post-Construction Cleaning">Post-Construction Cleaning</option>
                      <option value="Specialized Cleaning">Specialized Facility Cleaning</option>
                      <option value="Professional Staffing">Professional Staffing</option>
                      <option value="Valet Parking">Valet Parking</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#03143A] mb-1.5 uppercase tracking-wide">Message</label>
                    <textarea name="message" rows={3} placeholder="Tell us about your facility and cleaning needs..." maxLength={500} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#03143A] focus:outline-none focus:border-[#C70532] transition-colors resize-none bg-[#FAF8F5]" />
                  </div>
                  {formState.status === 'error' && (
                    <p className="text-red-500 text-xs">Something went wrong. Please try again or call (713) 338-2553.</p>
                  )}
                  <button
                    type="submit"
                    disabled={formState.status === 'loading'}
                    className="w-full py-4 bg-[#C70532] hover:bg-[#a80428] disabled:bg-gray-300 text-white font-black rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-base tracking-wide"
                  >
                    {formState.status === 'loading' ? 'Submitting...' : 'Submit & Get Your Free Quote'}
                  </button>
                  <p className="text-center text-gray-400 text-xs">No spam. No obligation. Response within 24 hours.</p>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
